import React, { useCallback, useState } from 'react';
import { ConfirmActionModal, WizardStepper } from '@shared/components';
import { QuoteErrorModal } from './QuoteErrorModal';
import { QuoteProgressNav } from './QuoteProgressNav';
import { CompletionErrorAlert } from './CompletionErrorAlert';
import { VehicleIdentityMopUpModal } from './VehicleIdentityMopUpModal';
import { PriceChangedModal } from './PriceChangedModal';
import { useAutoIncludedAddOns } from '@features/service-plans/hooks/useAutoIncludedAddOns';
import { useQuote } from '../context/QuoteContext';
import { useQuoteSteps } from '../hooks/useQuoteSteps';
import { useQuoteAdvance } from '../hooks/useQuoteAdvance';
import { useQuotePaymentJourney } from '../hooks/useQuotePaymentJourney';
import { useReconfirmToast } from '../hooks/useReconfirmToast';
import { useAgreementActivation } from '../hooks/useAgreementActivation';
import { useVehicleIdentityGate } from '../hooks/useVehicleIdentityGate';
import { quoteErrorPresentation, resolveQuoteErrorMessage } from '../services/quoteErrorMessage';
import { paymentNavControl } from '../utils/paymentNavControl';
import { buildStartOverMessage } from '../utils/startOverMessage';
import { DealerSelectionStep } from './steps/DealerSelectionStep';
import { VehicleSelectionStep } from './steps/VehicleSelectionStep';
import { ProductSelectionStep } from './steps/ProductSelectionStep';
import { ConfigurationStep } from './steps/ConfigurationStep';
import { CustomerSelectionStep } from './steps/CustomerSelectionStep';
import { PaymentStep } from './steps/PaymentStep';
import { QuoteSummaryStep } from './steps/QuoteSummaryStep';
import { ElemisCheckoutModal } from './ElemisCheckoutModal';
import { PaymentCollectionModal } from './PaymentCollectionModal';
import { getProductCurrency } from '@/features/products/services/productCurrency';
import { useCompleteSale } from '../hooks/useCompleteSale';
import { useAgreementConfirmation } from '../hooks/useAgreementConfirmation';
import { useAuth } from '@/features/auth';
import { useNavigate } from 'react-router-dom';

export const QuoteWizard: React.FC = () => {
    const navigate = useNavigate();
    const quote = useQuote();
    const { assignmentsPending } = useAutoIncludedAddOns();
    const { steps, currentStepIndex, stepPosition } = useQuoteSteps();
    const { saveLane } = quote;
    const {
        priceChange,
        quoteError,
        isErrorModalOpen,
        openErrorModal,
        closeErrorModal,
        retryQuoteSave,
    } = saveLane;
    const {
        completeSale,
        isLoading: completingSale,
        error: completionError,
        clearError,
    } = useCompleteSale({ onPriceChanged: priceChange.open, openErrorModal });
    const advance = useQuoteAdvance();

    const runCompleteSale = useCallback(async () => {
        if (quote.canCompleteQuote()) {
            try {
                await completeSale({ quoteId: quote.quoteId! });
            } catch {
                // useCompleteSale already surfaced this error (error state, toast, step routing)
                // before re-throwing; nothing is left for the caller to do.
            }
        }
    }, [quote, completeSale]);

    // Intercepts both terminal actions when the vehicle has neither a VIN nor a VRM;
    // a failed flush is surfaced by the save lane.
    const identityGate = useVehicleIdentityGate({
        vehicle: quote.selectedVehicle,
        flushSave: saveLane.flush,
    });

    // The single payment-journey instance: QuoteProgressNav renders twice from one prop object.
    const {
        elemisCheckout,
        paymentCollectionGate,
        handleContinueToPayment,
        continueBlockReason,
        canPressContinue,
        reconfirmRequired,
        customerName, contacts, liveCheckoutHeld,
    } = useQuotePaymentJourney({
        completeSale: runCompleteSale,
        completingSale,
        assignmentsPending,
        identityGate,
    });
    const { reshow: reshowReconfirm } = useReconfirmToast({
        required: reconfirmRequired,
        currentStep: quote.currentStep,
        goToStep: quote.goToStep,
    });
    const initiateCheckout = reconfirmRequired ? reshowReconfirm : handleContinueToPayment;
    const retryCheckout = reconfirmRequired ? reshowReconfirm : paymentCollectionGate.retry;
    const { user } = useAuth();

    // The frame's `completed` report starts this; only the server's answers
    // move it forward.
    const activation = useAgreementActivation(
        elemisCheckout.agreementId,
        quote.quoteId,
        elemisCheckout.outcome === 'completed' && quote.status !== 'completed',
    );

    // The single instance, enabled only once completion has already resolved, so it can
    // never delay, fail or alter the success state.
    const agreementConfirmation = useAgreementConfirmation(quote.quoteId, quote.status === 'completed');

    const stepLabels = steps.map((step) => step.title);

    const { title: errorTitle, canRetry } = quoteErrorPresentation(quoteError?.status);

    const renderCurrentStep = () => {
        switch (quote.currentStep) {
            case 'dealer':
                return <DealerSelectionStep onAdvance={advance} />;
            case 'vehicle':
                return <VehicleSelectionStep />;
            case 'product':
                return <ProductSelectionStep onAdvance={advance} />;
            case 'configuration':
                return <ConfigurationStep />;
            case 'customer':
                return <CustomerSelectionStep onSaveQuote={saveLane.flush} canSaveQuote={saveLane.canSave} />;
            case 'payment':
                return <PaymentStep />;
            case 'summary':
                // The hosted checkout opens as ElemisCheckoutModal over this
                // step once a checkoutUrl exists; the summary stays behind it.
                return (
                    <QuoteSummaryStep
                        journey={elemisCheckout.journey}
                        isInitiating={elemisCheckout.isInitiating}
                        refusal={elemisCheckout.refusal}
                        checkoutStarted={elemisCheckout.checkoutUrl !== null}
                        agreementRef={elemisCheckout.agreementRef}
                        linkSuppressedByReplay={elemisCheckout.linkSuppressedByReplay}
                        outcome={elemisCheckout.outcome}
                        activation={activation}
                        onInitiateCheckout={initiateCheckout}
                        onRetryCheckout={retryCheckout}
                        retryBusy={paymentCollectionGate.isBusy}
                        agreementConfirmation={agreementConfirmation}
                    />
                );
            default:
                return <DealerSelectionStep />;
        }
    };

    const handleSaveQuote = () => {
        if (saveLane.canSave) saveLane.requestSave();
    };

    // Seeding can still add a mandatory line, so advancing now would save the quote without it.
    const awaitingAutoIncludes = quote.currentStep === 'configuration' && assignmentsPending;

    const handleNext = () => {
        if (awaitingAutoIncludes) return;
        advance();
    };

    const handlePrev = () => {
        if (quote.canGoBack) {
            handleSaveQuote();
            quote.previousStep();
        }
    };

    const [isStartOverConfirmOpen, setIsStartOverConfirmOpen] = useState(false);

    const requestStartOver = () => setIsStartOverConfirmOpen(true);
    const cancelStartOver = () => setIsStartOverConfirmOpen(false);

    // quote.reset() is Redux-only and QuoteWizard does not unmount, so clearError() is what
    // stops a completion-refusal banner surviving onto the blank new journey (#876/D6).
    const confirmStartOver = () => {
        setIsStartOverConfirmOpen(false);
        clearError();
        quote.reset();
    };

    const handleNavigateToAgreement = () => {
        // The path is built before reset() because reset() clears quoteId.
        if (!quote.quoteId) return;
        const agreementPath = `/agreements/quote/${quote.quoteId}`;
        quote.reset();
        navigate(agreementPath);
    };

    const handleNavigateToAgreementsList = () => {
        quote.reset();
        navigate(`/agreements`);
    };

    // The single reason that matches the branch QuoteProgressNav will render.
    const blockReason =
        quote.currentStep !== 'summary' ? quote.advanceBlockReason : continueBlockReason;

    const paymentControl = paymentNavControl({
        outcome: elemisCheckout.outcome,
        journey: elemisCheckout.journey,
        liveCheckoutHeld,
    });

    const progressNavProps = {
        stepPosition,
        currentStep: quote.currentStep,
        status: quote.status,
        canGoBack: quote.canGoBack,
        canProceed: quote.canProceed && !awaitingAutoIncludes,
        canInitiateCheckout: canPressContinue,
        initiatingCheckout: elemisCheckout.isInitiating || paymentCollectionGate.isBusy,
        paymentControl,
        onInitiateCheckout: initiateCheckout,
        onRetryCheckout: retryCheckout,
        onPrev: handlePrev,
        onNext: handleNext,
        onRequestStartOver: requestStartOver,
        onNavigateToAgreementsList: handleNavigateToAgreementsList,
        onNavigateToAgreement: handleNavigateToAgreement,
        blockReason,
        agreementConfirmationMissing: agreementConfirmation.state === 'missing',
    };

    return (
        <>
            {/* Progress Indicator */}
            <div className="mb-8">
                <WizardStepper
                    stages={stepLabels}
                    currentStep={currentStepIndex}
                    onNext={handleNext}
                    onPrev={handlePrev}
                >
                    {/* Top progress + navigation */}
                    <div className="mt-4">
                        <QuoteProgressNav {...progressNavProps} />
                    </div>

                    {/* #876. Mounted OUTSIDE renderCurrentStep() so a completion
                        refusal's explanation survives the setCurrentStep dispatch that
                        routes the user to the failing step — see CompletionErrorAlert's
                        docblock and the plan's D3. */}
                    {completionError && (
                        <div className="mt-4">
                            <CompletionErrorAlert message={completionError} />
                        </div>
                    )}

                    <div className="bg-mp-surface rounded-lg shadow-sm border border-mp-border p-6 my-8">
                        {renderCurrentStep()}
                    </div>
                </WizardStepper>

                {/* Bottom progress + navigation */}
                <div className="mt-4">
                    <QuoteProgressNav {...progressNavProps} />
                </div>

                {/* Show quote status */}
                <div className="mt-2 flex justify-between text-xs text-mp-muted">
                    <span>
                        {quote.quoteRef ? `Quote Ref: ${quote.quoteRef}` : 'Quote not saved'}
                    </span>
                    <span>
                        {quote.status !== 'completed'
                            ? saveLane.canSave
                                ? 'Ready to save'
                                : 'Complete all steps to save'
                            : 'Quote completed'}
                    </span>
                </div>
            </div>

            {/* Error Modal */}
            <QuoteErrorModal
                isOpen={isErrorModalOpen}
                onClose={closeErrorModal}
                title={errorTitle}
                message={resolveQuoteErrorMessage(quoteError, {
                    dealerName: quote.selectedDealer?.name,
                    productName: quote.selectedProduct?.name,
                })}
                status={quoteError?.status}
                canRetry={canRetry}
                onRetry={canRetry ? retryQuoteSave : undefined}
            />

            <ConfirmActionModal
                isOpen={isStartOverConfirmOpen}
                title="Start over?"
                message={buildStartOverMessage(quote.quoteRef)}
                confirmLabel="Discard and start over"
                onConfirm={confirmStartOver}
                onClose={cancelStartOver}
            />

            <PriceChangedModal
                state={priceChange.state}
                isPending={priceChange.isPending}
                onConfirm={() => void priceChange.confirm((override) => saveLane.save({ pricingOverride: override }))}
                onClose={priceChange.close}
            />

            <VehicleIdentityMopUpModal
                isOpen={identityGate.isOpen}
                intent={identityGate.pendingIntent}
                isSaving={identityGate.isSaving}
                onSubmit={(identity) => void identityGate.submit(identity)}
                onClose={identityGate.cancel}
            />

            {paymentCollectionGate.mode.kind !== 'none' && (
                <PaymentCollectionModal
                    isOpen={paymentCollectionGate.isOpen}
                    mode={paymentCollectionGate.mode}
                    currency={getProductCurrency(quote.selectedProduct)}
                    contacts={contacts}
                    liveCheckoutHeld={liveCheckoutHeld}
                    isBusy={paymentCollectionGate.isBusy}
                    userName={user?.name}
                    customerName={customerName}
                    onCancel={paymentCollectionGate.cancel}
                    onConfirm={paymentCollectionGate.confirm}
                />
            )}

            {elemisCheckout.checkoutUrl !== null && quote.status !== 'completed' && (
                <ElemisCheckoutModal
                    isOpen={elemisCheckout.isCheckoutOpen}
                    onClose={elemisCheckout.closeCheckout}
                    checkoutUrl={elemisCheckout.checkoutUrl}
                    applicationId={elemisCheckout.applicationId}
                    onOutcome={elemisCheckout.receiveOutcome}
                />
            )}
        </>
    );
};
